import i18n from '../i18n/config';
import { useTranslation } from 'react-i18next';
// frontend/src/pages/TaskDetailPage.jsx
import React, { useEffect, useState } from 'react';
import {
    Alert, Avatar, Box, Button, Card, CardContent, Chip, CircularProgress, Divider,
    Grid, IconButton, List, ListItem, ListItemAvatar, ListItemText, Paper, Stack,
    TextField, Typography
} from '@mui/material';
import {
    ArrowBack, Send as SendIcon, History as HistoryIcon, Comment as CommentIcon,
    PlayArrow as StartIcon, AssignmentTurnedIn as DoneIcon, Cancel as CancelIcon,
    AutoMode as AutoIcon, Person as PersonIcon, WarningAmber as WarningIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import TaskNotifyDialog from '../components/tasks/TaskNotifyDialog';
import { useNotification } from '../components/common/GlobalNotifications';

// Etiquetas resueltas al renderizar: como constantes de modulo quedaban
// congeladas en el idioma presente al importar el archivo (el idioma del perfil
// se aplica despues y cambiarlo no recarga la pagina).
const STATUS_LABEL_KEYS = { pending: 'hardcoded.components_admin_ordermanagement.pendiente', in_progress: 'hardcoded.pages_checkoutpage.en_progreso', blocked: 'hardcoded.components_tickets_ticketactioncenter.bloqueada', completed: 'hardcoded.components_tickets_ticketactioncenter.completada', cancelled: 'hardcoded.components_admin_ordermanagement.cancelada' };
const statusLabel = (status) => (STATUS_LABEL_KEYS[status] ? i18n.t(STATUS_LABEL_KEYS[status]) : status);
const STATUS_COLORS = { pending: 'default', in_progress: 'info', blocked: 'warning', completed: 'success', cancelled: 'default' };
const PRIORITY_LABEL_KEYS = { low: 'hardcoded.pages_taskdetailpage.baja', medium: 'hardcoded.pages_taskdetailpage.media', high: 'hardcoded.pages_alarmticketpage.alta', critical: 'alarmTickets.severity.critical' };
const priorityLabel = (priority) => (PRIORITY_LABEL_KEYS[priority] ? i18n.t(PRIORITY_LABEL_KEYS[priority]) : priority);
const PRIORITY_COLORS = { low: 'default', medium: 'info', high: 'warning', critical: 'error' };
const formatDate = (iso) => iso ? new Date(iso).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' }) : '—';

const TaskDetailPage = () => {
  const { t } = useTranslation();
    const { taskId } = useParams();
    const navigate = useNavigate();
    const { showError, showSuccess } = useNotification();
    const [task, setTask] = useState(null);
    const [loading, setLoading] = useState(true);
    const [commentBody, setCommentBody] = useState('');
    const [sending, setSending] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.tasks.get(taskId);
            setTask(r.data);
        } catch (e) {
            showError(t('hardcoded.pages_taskdetailpage.no_se_pudo_cargar_la'));
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); }, [taskId]); // eslint-disable-line

    const sendComment = async () => {
        if (!commentBody.trim()) return;
        setSending(true);
        try {
            await api.tasks.comment(taskId, commentBody.trim());
            setCommentBody('');
            await load();
        } catch (e) {
            showError(e?.response?.data?.detail || t('hardcoded.pages_taskdetailpage.no_se_pudo_enviar_el'));
        } finally { setSending(false); }
    };

    const transition = async (status) => {
        try {
            await api.tasks.transition(taskId, status);
            showSuccess(t('hardcoded.pages_taskdetailpage.tarea_status_labelsst', { STATUS_LABELSst: statusLabel(status) }));
            load();
        } catch (e) { showError(e?.response?.data?.detail || t('hardcoded.pages_taskdetailpage.error_transicion')); }
    };

    const [notifyKind, setNotifyKind] = useState(null);

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>;
    if (!task) return <Alert severity="error">{t('hardcoded.pages_taskdetailpage.tarea_no_encontrada')}</Alert>;

    return (
        <Box sx={{ pb: 4 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <IconButton onClick={() => navigate('/tasks')}><ArrowBack /></IconButton>
                <Box sx={{ flex: 1 }}>
                    <Typography variant="h5" fontWeight={700}>{task.title}</Typography>
                    <Typography variant="caption" color="text.secondary">{i18n.t('hardcoded.pages_taskdetailpage.creada')}{formatDate(task.created_at)}</Typography>
                </Box>
                <Chip size="small" color={STATUS_COLORS[task.status] || 'default'} label={statusLabel(task.status)} />
                <Chip size="small" color={PRIORITY_COLORS[task.priority] || 'default'} label={t('hardcoded.pages_taskdetailpage.prioridad_priority_labels', { PRIORITY_LABELS: priorityLabel(task.priority) })} />
                {task.origin === 'automatic' && <Chip size="small" icon={<AutoIcon />} label={t('hardcoded.components_settings_adoptionnotificationpreferences.automatica')} color="info" variant="outlined" />}
                {/* Se pregunta el canal (correo / WhatsApp) antes de enviar. */}
                <Button size="small" variant="outlined" startIcon={<SendIcon />}
                    onClick={() => setNotifyKind('assigned')}>
                    Notificar
                </Button>
            </Stack>

            <TaskNotifyDialog
                open={Boolean(notifyKind)}
                taskId={taskId}
                kind={notifyKind || 'assigned'}
                onClose={() => setNotifyKind(null)}
            />

            <Grid container spacing={2}>
                <Grid item xs={12} md={8}>
                    {/* Descripción */}
                    <Paper sx={{ p: 2, mb: 2 }}>
                        <Typography variant="subtitle2" fontWeight={700} gutterBottom>{i18n.t('audit.table.description')}</Typography>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{task.description || <em>{t('hardcoded.pages_billingplanspage.sin_descripcion')}</em>}</Typography>
                        {task.is_overdue && (
                            <Alert severity="error" icon={<WarningIcon />} sx={{ mt: 2 }}>
                                {t('hardcoded.pages_taskdetailpage.tarea_vencida_desde')}{formatDate(task.due_at)}
                            </Alert>
                        )}
                    </Paper>

                    {/* Comentarios */}
                    <Paper sx={{ p: 2, mb: 2 }}>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                            <CommentIcon color="primary" />
                            <Typography variant="subtitle2" fontWeight={700}>{i18n.t('hardcoded.pages_taskdetailpage.comentarios')}{task.comments?.length || 0})</Typography>
                        </Stack>
                        <Stack spacing={1} sx={{ mb: 2 }}>
                            {(task.comments || []).map((c) => (
                                <Box key={c.id} sx={{ display: 'flex', gap: 1 }}>
                                    <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>{(c.user_name || '?')[0]}</Avatar>
                                    <Box sx={{ flex: 1 }}>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <Typography variant="body2" fontWeight={600}>{c.user_name || t('audit.table.user')}</Typography>
                                            <Typography variant="caption" color="text.secondary">{formatDate(c.created_at)}</Typography>
                                        </Stack>
                                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{c.body}</Typography>
                                    </Box>
                                </Box>
                            ))}
                            {!(task.comments?.length) && <Alert severity="info">{t('hardcoded.pages_taskdetailpage.aun_no_hay_comentarios')}</Alert>}
                        </Stack>
                        <Stack direction="row" spacing={1}>
                            <TextField size="small" fullWidth placeholder={t('hardcoded.pages_taskdetailpage.escribe_un_comentario')} value={commentBody}
                                onChange={(e) => setCommentBody(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendComment(); } }} />
                            <Button variant="contained" disabled={sending || !commentBody.trim()} startIcon={sending ? <CircularProgress size={14} /> : <SendIcon />} onClick={sendComment}>
                                {t('hardcoded.components_chat_videonoterecorder.enviar')}</Button>
                        </Stack>
                    </Paper>

                    {/* Historial */}
                    <Paper sx={{ p: 2 }}>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                            <HistoryIcon color="primary" />
                            <Typography variant="subtitle2" fontWeight={700}>{i18n.t('hardcoded.components_ai_assistantpanel.historial')}</Typography>
                        </Stack>
                        <List dense>
                            {(task.history || []).map((h) => (
                                <ListItem key={h.id} sx={{ px: 0 }}>
                                    <ListItemText
                                        primary={`${h.action}${h.note ? ' — ' + h.note : ''}`}
                                        secondary={`${h.user_name || t('hardcoded.pages_taskdetailpage.sistema')} · ${formatDate(h.created_at)}`}
                                    />
                                </ListItem>
                            ))}
                        </List>
                    </Paper>
                </Grid>

                <Grid item xs={12} md={4}>
                    {/* Acciones rápidas */}
                    <Paper sx={{ p: 2, mb: 2 }}>
                        <Typography variant="subtitle2" fontWeight={700} gutterBottom>{t('common.actions')}</Typography>
                        <Stack spacing={1}>
                            {task.status !== 'in_progress' && task.status !== 'completed' && task.status !== 'cancelled' && (
                                <Button fullWidth variant="outlined" startIcon={<StartIcon />} onClick={() => transition('in_progress')}>{i18n.t('hardcoded.pages_taskdetailpage.iniciar')}</Button>
                            )}
                            {task.status !== 'completed' && task.status !== 'cancelled' && (
                                <Button fullWidth variant="contained" color="success" startIcon={<DoneIcon />} onClick={() => transition('completed')}>{i18n.t('hardcoded.pages_taskdetailpage.completar')}</Button>
                            )}
                            {task.status !== 'cancelled' && task.status !== 'completed' && (
                                <Button fullWidth variant="outlined" color="error" startIcon={<CancelIcon />} onClick={() => transition('cancelled')}>{t('common.cancel')}</Button>
                            )}
                        </Stack>
                    </Paper>

                    {/* Información lateral */}
                    <Paper sx={{ p: 2, mb: 2 }}>
                        <Typography variant="subtitle2" fontWeight={700} gutterBottom>{t('hardcoded.pages_taskdetailpage.informacion')}</Typography>
                        <List dense>
                            <ListItem sx={{ px: 0 }}><ListItemText primary={t('hardcoded.pages_taskdetailpage.asignado')} secondary={task.assignee_name || t('sensors.stats.unassigned')} /></ListItem>
                            <ListItem sx={{ px: 0 }}><ListItemText primary={t('hardcoded.pages_taskdetailpage.creado_por')} secondary={task.creator_name || '—'} /></ListItem>
                            <ListItem sx={{ px: 0 }}><ListItemText primary={t('hardcoded.components_admin_categoryform.categoria')} secondary={task.category} /></ListItem>
                            <ListItem sx={{ px: 0 }}><ListItemText primary={t('hardcoded.pages_billinginvoicespage.vencimiento')} secondary={formatDate(task.due_at)} /></ListItem>
                            <ListItem sx={{ px: 0 }}><ListItemText primary={t('vehicles.table.vehicle')} secondary={task.vehicle_name || '—'} /></ListItem>
                            <ListItem sx={{ px: 0 }}><ListItemText primary={t('hardcoded.pages_taskdetailpage.posicion_eje')} secondary={task.position ? t('hardcoded.pages_taskdetailpage.pos_position_eje_taskaxle', { position: task.position, taskaxle: task.axle ?? '—' }) : '—'} /></ListItem>
                            {task.ticket_number && <ListItem sx={{ px: 0 }}><ListItemText primary={t('hardcoded.pages_taskdetailpage.ticket')} secondary={task.ticket_number} /></ListItem>}
                            {task.profile_name && <ListItem sx={{ px: 0 }}><ListItemText primary={t('hardcoded.pages_taskdetailpage.perfil')} secondary={task.profile_name} /></ListItem>}
                            {task.rule_name && <ListItem sx={{ px: 0 }}><ListItemText primary={t('hardcoded.pages_taskdetailpage.regla_automatica')} secondary={task.rule_name} /></ListItem>}
                        </List>
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
};

export default TaskDetailPage;
